import template from 'lodash-es/template';
import { oneLine, oneLineTrim } from 'common-tags';
import budgetSelectorOptionsTemplate from './template-budget-selector-options.html';

/**
 * Class to easily add budget selection functionality to forms (like many of
 * our Post-Project forms). Note that this class does not take care of initial
 * rendering of these form elements and will only manage user interaction and
 * interactive functionality.
 */
export default class ProjectFormBudgetDetails {
  /**
   * @param {HTMLSelectElement} options.currencySelectorEl
   * @param {HTMLSelectElement} options.budgetSelectorEl
   * @param {boolean} [options.supportHourlyBudgetPeriod=true]
   * @param {HTMLInputElement} [options.fixedPeriodRadioEl=null]
   * @param {HTMLInputElement} [options.hourlyPeriodRadioEl=null]
   * @param {HTMLElement}
   *    [hourlyElementsContainerEl=document.createElement('div')]
   * @param {HTMLSelectElement}
   *    [options.hourlyDurationSelectorEl=document.createElement('div')]
   * @param {HTMLInputElement}
   *    [options.hourlyCommitmentInputEl=document.createElement('div')]
   * @param {HTMLSelectElement}
   *    [hourlyCommitmentIntervalSelectorEl=document.createElement('div')]
   * @param {HTMLSelectElement}
   *    [hourlyCommitmentUndefinedCheckboxEl=document.createElement('div')]
   */
  constructor(options) {
    this.currencySelectorEl = options.currencySelectorEl;
    this.budgetSelectorEl = options.budgetSelectorEl;
    this.supportHourlyBudgetPeriod = options.supportHourlyBudgetPeriod || true;
    // Hourly-budget mode related params
    this.fixedPeriodRadioEl = options.fixedPeriodRadioEl || null;
    this.hourlyPeriodRadioEl = options.hourlyPeriodRadioEl || null;
    this.hourlyElementsContainerEl =
      options.hourlyElementsContainerEl || document.createElement('div');
    this.hourlyDurationSelectorEl =
      options.hourlyDurationSelectorEl || document.createElement('div');
    this.hourlyCommitmentInputEl =
      options.hourlyCommitmentInputEl || document.createElement('div');
    this.hourlyCommitmentIntervalSelectorEl =
      options.hourlyCommitmentIntervalSelectorEl ||
      document.createElement('div');
    this.hourlyCommitmentUndefinedCheckboxEl =
      options.hourlyCommitmentUndefinedCheckboxEl ||
      document.createElement('div');

    // Internal view state
    this.state = {
      currency: options.defaultCurrency || {},
      fixedBudgets: options.defaultFixedBudgets || [],
      hourlyBudgets: options.defaultHourlyBudgets || [],
      isHourlyPeriodMode: false,
    };

    this._addEventListeners();
  }

  _addEventListeners() {
    if (this.fixedPeriodRadioEl) {
      this.fixedPeriodRadioEl.addEventListener('change', () => {
        this.deactivateHourlyPeriodMode();
      });
    }

    if (this.hourlyPeriodRadioEl) {
      this.hourlyPeriodRadioEl.addEventListener('change', () => {
        this.activateHourlyPeriodMode();
      });
    }

    this.currencySelectorEl.addEventListener('change', event => {
      const currencyId = event.target.value;
      this._onCurrencySelected(currencyId);
    });
  }

  _onCurrencySelected(currencyId) {
    const currencyPromise = ProjectFormBudgetDetails.fetchCurrencyDetails(
      currencyId,
    );
    const fixedBudgetsPromise = ProjectFormBudgetDetails.fetchFixedBudgetsForCurrency(
      currencyId,
    );
    const hourlyBudgetsPromise = ProjectFormBudgetDetails.fetchHourlyBudgetsForCurrency(
      currencyId,
    );
    Promise.all([
      currencyPromise,
      fixedBudgetsPromise,
      hourlyBudgetsPromise,
    ]).then(responseArray => {
      this.state.currency = responseArray[0].currencyArray;
      this.state.fixedBudgets = responseArray[1].vars.budgets;
      this.state.hourlyBudgets = responseArray[2].vars.budgets;
      this.renderBudgetSelectorOptions();
    });
  }

  renderBudgetSelectorOptions() {
    const budgetList = this.isInHourlyPeriodMode()
      ? this.state.hourlyBudgets
      : this.state.fixedBudgets;
    // Prepare data for the template and render
    const budgetListToView = budgetList.map(budget => {
      const amountText = budget.max_sum
        ? `${budget.min_sum}-${budget.max_sum}`
        : ` >${budget.min_sum}`;
      budget.text = oneLine`${budget.name}
        (${this.state.currency.sign}${amountText} ${this.state.currency.code})`;
      return budget;
    });
    this.budgetSelectorEl.innerHTML = template(budgetSelectorOptionsTemplate)({
      budgets: budgetListToView,
    });
  }

  isInHourlyPeriodMode() {
    return this.state.isHourlyPeriodMode;
  }

  selectCurrency(currencyId) {
    this.currencySelectorEl.value = currencyId;
    this._onCurrencySelected(currencyId);
  }

  /**
   * Will do nothing and return false if `options.supportHourlyBudgetPeriod`
   * got set to false at constructor
   */
  deactivateHourlyPeriodMode() {
    if (!this.supportHourlyBudgetPeriod) {
      return false;
    }

    // Hide the hourly budget elements
    this.hourlyElementsContainerEl.classList.add('is-hidden');
    // Disable inputs
    this.hourlyDurationSelectorEl.setAttribute('disabled', 'disabled');
    this.hourlyCommitmentInputEl.setAttribute('disabled', 'disabled');
    this.hourlyCommitmentIntervalSelectorEl.setAttribute(
      'disabled',
      'disabled',
    );
    this.hourlyCommitmentUndefinedCheckboxEl.setAttribute(
      'disabled',
      'disabled',
    );
    this.state.isHourlyPeriodMode = false;
    // Re-render budget options
    this.renderBudgetSelectorOptions();
  }

  /**
   * Will do nothing and return false if `options.supportHourlyBudgetPeriod`
   * got set to false at constructor
   */
  activateHourlyPeriodMode() {
    if (!this.supportHourlyBudgetPeriod) {
      return false;
    }

    // Disable inputs
    this.hourlyDurationSelectorEl.removeAttribute('disabled');
    this.hourlyCommitmentInputEl.removeAttribute('disabled');
    this.hourlyCommitmentIntervalSelectorEl.removeAttribute('disabled');
    this.hourlyCommitmentUndefinedCheckboxEl.removeAttribute('disabled');
    // Show the hourly budget elements
    this.hourlyElementsContainerEl.classList.remove('is-hidden');
    this.state.isHourlyPeriodMode = true;
    // Re-render budget options
    this.renderBudgetSelectorOptions();
  }
}

ProjectFormBudgetDetails.fetchHourlyBudgetsForCurrency = currencyId =>
  fetch(
    oneLineTrim`/buyers/on-budget-control.php?
    budgetPeriod=hourly&currency=${currencyId}`,
  ).then(res => res.json());

ProjectFormBudgetDetails.fetchFixedBudgetsForCurrency = currencyId =>
  fetch(
    oneLineTrim`/buyers/on-budget-control.php?
    budgetPeriod=fixed&currency=${currencyId}`,
  ).then(res => res.json());

ProjectFormBudgetDetails.fetchCurrencyDetails = currencyId =>
  fetch(
    `/buyers/on-currency-control.php?raw=true&currency=${currencyId}`,
  ).then(res => res.json());

ProjectFormBudgetDetails.fetchIpBasedCurrency = () =>
  fetch('/ajax/postproject/ip-currency.php').then(res => res.json());
